/**
 * Analytics Formulas Service - Standardized calculations for social media metrics
 */
const analyticsFormulasService = {
  /**
   * Calculate Engagement Rate using the standard formula
   * Engagement Rate (%) = (Likes + Comments + Shares + Saves) ÷ Views × 100
   * @param {object} metrics - Object containing likes, comments, shares, saves, views
   * @returns {number} - Engagement rate as a percentage (0-100)
   */
  calculateEngagementRate: (metrics) => {
    const { likes = 0, comments = 0, shares = 0, saves = 0, views = 0 } = metrics;
    
    if (views === 0) return 0;
    
    const totalEngagements = likes + comments + shares + saves;
    const engagementRate = (totalEngagements / views) * 100;
    
    return Math.round(engagementRate * 100) / 100; // Round to 2 decimal places
  },

  /**
   * Calculate Click-Through Rate (CTR)
   * CTR (%) = (Clicks ÷ Impressions) × 100
   * @param {number} clicks - Number of clicks
   * @param {number} impressions - Number of impressions/views
   * @returns {number} - CTR as a percentage
   */
  calculateCTR: (clicks, impressions) => {
    if (impressions === 0) return 0;
    return Math.round(((clicks / impressions) * 100) * 100) / 100;
  },

  /**
   * Calculate Cost Per Engagement (CPE)
   * CPE = Total Spend ÷ Total Engagements
   * @param {number} totalSpend - Total campaign spend
   * @param {number} totalEngagements - Total engagements
   * @returns {number} - Cost per engagement
   */
  calculateCPE: (totalSpend, totalEngagements) => {
    if (totalEngagements === 0) return 0;
    return Math.round((totalSpend / totalEngagements) * 100) / 100;
  },

  /**
   * Calculate Cost Per Mille (CPM) - Cost per 1000 impressions
   * CPM = (Total Spend ÷ Impressions) × 1000
   * @param {number} totalSpend - Total campaign spend
   * @param {number} impressions - Total impressions
   * @returns {number} - CPM
   */
  calculateCPM: (totalSpend, impressions) => {
    if (impressions === 0) return 0;
    return Math.round(((totalSpend / impressions) * 1000) * 100) / 100;
  },

  /**
   * Calculate Return on Ad Spend (ROAS)
   * ROAS = Revenue ÷ Ad Spend
   * @param {number} revenue - Revenue generated
   * @param {number} adSpend - Amount spent on ads
   * @returns {number} - ROAS ratio
   */
  calculateROAS: (revenue, adSpend) => {
    if (adSpend === 0) return 0;
    return Math.round((revenue / adSpend) * 100) / 100;
  },

  /**
   * Calculate Reach Rate
   * Reach Rate (%) = (Reach ÷ Total Audience) × 100
   * @param {number} reach - Number of unique users reached
   * @param {number} totalAudience - Total potential audience
   * @returns {number} - Reach rate as a percentage
   */
  calculateReachRate: (reach, totalAudience) => {
    if (totalAudience === 0) return 0;
    return Math.round(((reach / totalAudience) * 100) * 100) / 100;
  },

  /**
   * Calculate Video View Rate
   * View Rate (%) = (Views ÷ Impressions) × 100
   * @param {number} views - Number of video views
   * @param {number} impressions - Number of impressions
   * @returns {number} - View rate as a percentage
   */
  calculateViewRate: (views, impressions) => {
    if (impressions === 0) return 0;
    return Math.round(((views / impressions) * 100) * 100) / 100;
  },

  /**
   * Calculate Average Engagement Per Post
   * @param {number} totalEngagements - Total engagements across all posts
   * @param {number} numberOfPosts - Number of posts
   * @returns {number} - Average engagement per post
   */
  calculateAvgEngagementPerPost: (totalEngagements, numberOfPosts) => {
    if (numberOfPosts === 0) return 0;
    return Math.round((totalEngagements / numberOfPosts) * 100) / 100;
  },

  /**
   * Calculate Engagement Quality Score
   * Weights: Comments (3x), Shares (2x), Likes (1x), Saves (2x)
   * @param {object} metrics - Object containing engagement metrics
   * @returns {number} - Quality score
   */
  calculateEngagementQuality: (metrics) => {
    const { likes = 0, comments = 0, shares = 0, saves = 0 } = metrics;
    
    const qualityScore = (comments * 3) + (shares * 2) + (likes * 1) + (saves * 2);
    return qualityScore;
  },

  /**
   * Calculate Platform-Specific Engagement Rates
   * @param {object} platformData - Data for each platform
   * @returns {object} - Engagement rates by platform
   */
  calculatePlatformEngagementRates: (platformData) => {
    const results = {};
    
    Object.keys(platformData).forEach(platform => {
      const data = platformData[platform];
      results[platform] = {
        ...data,
        engagementRate: analyticsFormulasService.calculateEngagementRate(data),
        ctr: analyticsFormulasService.calculateCTR(data.clicks || 0, data.views || 0),
        avgEngagementPerPost: analyticsFormulasService.calculateAvgEngagementPerPost(
          (data.likes || 0) + (data.comments || 0) + (data.shares || 0) + (data.saves || 0),
          data.posts || 1
        )
      };
    });
    
    return results;
  },

  /**
   * Get engagement rate performance level
   * @param {number} engagementRate - Engagement rate percentage
   * @param {string} platform - Platform name (instagram, facebook, tiktok)
   * @returns {object} - Performance level and color
   */
  getEngagementPerformanceLevel: (engagementRate, platform = 'instagram') => {
    // Industry benchmarks by platform
    const benchmarks = {
      instagram: { excellent: 6, good: 3, average: 1 },
      facebook: { excellent: 0.9, good: 0.5, average: 0.2 },
      tiktok: { excellent: 9, good: 6, average: 3 }
    };
    
    const benchmark = benchmarks[platform.toLowerCase()] || benchmarks.instagram;
    
    if (engagementRate >= benchmark.excellent) {
      return { level: 'Excellent', color: 'green', description: 'Outstanding performance' };
    } else if (engagementRate >= benchmark.good) {
      return { level: 'Good', color: 'blue', description: 'Above average performance' };
    } else if (engagementRate >= benchmark.average) {
      return { level: 'Average', color: 'yellow', description: 'Average performance' };
    } else {
      return { level: 'Below Average', color: 'red', description: 'Needs improvement' };
    }
  },

  /**
   * Calculate trend percentage change
   * @param {number} current - Current period value
   * @param {number} previous - Previous period value
   * @returns {object} - Trend data with percentage and direction
   */
  calculateTrend: (current, previous) => {
    if (previous === 0) {
      return { percentage: current > 0 ? 100 : 0, direction: current > 0 ? 'up' : 'neutral' };
    }
    
    const percentage = Math.round(((current - previous) / previous) * 100 * 100) / 100;
    const direction = percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral';
    
    return { percentage: Math.abs(percentage), direction };
  }
};

export default analyticsFormulasService;
